import clsx from 'clsx'
import MultipleSelect from 'components/MultipleSelect'
import Select from 'components/Select'
import TableSticky from 'components/Table/sticky'
import { Button, Container, Grid, Icon, Spinner } from 'gu-ui-kit'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from 'modules/Statistics/Layout/Table/statistics-table.module.scss'
import downloadFile from 'utils/downloadFile'
import { notificationShow } from 'modules/Notifications/actions'
import { useDispatch } from 'react-redux'

const TableComponent = ({
  data,
  pagination = {},
  loading,
  filters,
  dropTableData = () => {},
  onTableParamsUpdate = () => {},
  groupBy,
  limit,
  tableSwitchers = [],
  exportButtons = [],
  columns,
  initialState = {},
  isDemoMode,
  onSortingChange = () => {},
}) => {
  const { t } = useTranslation(['statistics'])
  const dispatch = useDispatch()
  const [page, setPage] = useState(1)
  const [filtersState, setFiltersState] = useState(
    omit(filters, ['include_vat', 'date_group_period']),
  )

  useEffect(() => {
    setPage(1)
    dropTableData()
  }, [filtersState, groupBy, limit, isDemoMode])

  useEffect(
    () =>
      onTableParamsUpdate({
        group_by: groupBy,
        limit: limit,
        offset: (page - 1) * limit,
      }),
    [filtersState, groupBy, page, limit, isDemoMode],
  )

  useEffect(() => {
    const omittedFilters = omit(filters, ['include_vat', 'date_group_period'])
    !isEqual(filtersState, omittedFilters) && setFiltersState(omittedFilters)
  }, [filters])

  const changePage = useCallback((val) => setPage(page + val), [page])

  const handleDownloadError = useCallback(
    (error) =>
      dispatch(
        notificationShow({
          type: 'warning',
          message: error,
          delay: 20000,
        }),
      ),
    [dispatch],
  )

  return (
    <>
      <Grid
        gap={8}
        alignItems={'center'}
        className={styles['table_switcher-panel']}
        wrap={false}
        justifyContent={'between'}
      >
        <Grid fluid={false} wrap={false} gap={8} alignItems={'center'}>
          {tableSwitchers.map(
            ({
              name,
              value,
              values,
              onChange,
              onReset,
              type = 'one',
              placeholder = '',
              listTitle = '',
            }) =>
              type === 'multiple' ? (
                <MultipleSelect
                  key={'table-switcher-' + name}
                  name={name}
                  value={value}
                  values={values}
                  placeholder={placeholder}
                  listTitle={listTitle}
                  apply={t('table_switcher.submit')}
                  dropSelection={t('table_switcher.reset')}
                  onChange={onChange}
                  onReset={onReset}
                  buttonClass={styles['table_switcher-button']}
                />
              ) : (
                <Select
                  key={'table-switcher-' + name}
                  name={name}
                  value={value}
                  values={values}
                  onChange={onChange}
                  buttonClass={styles['table_switcher-button']}
                />
              ),
          )}
        </Grid>
        {exportButtons?.length > 0 && (
          <Grid fluid={false} wrap={false} gap={8} alignItems={'center'}>
            {exportButtons.map(({ type, fileUrl }) => (
              <Button
                onClick={() =>
                  downloadFile(fileUrl, 'statistics', type).catch(
                    handleDownloadError,
                  )
                }
                key={type}
                variant={'secondary'}
                grey
                size={'m'}
                startIcon={<Icon name={'file-' + type} />}
              >
                {t('statistics::buttons.download.label')}
              </Button>
            ))}
          </Grid>
        )}
      </Grid>
      <TableSticky
        tableData={data}
        tableLoading={loading}
        columns={columns}
        className={clsx(styles.table, 'sticky')}
        showFooter
        tableConfig={{
          disableMultiSort: true,
          autoResetSortBy: false,
          initialState: initialState,
        }}
        onSortingChange={onSortingChange}
      />

      {data.length > 0 && (
        <div className={styles['pagination']}>
          {!!pagination?.total && (
            <span className={styles['pagination__from-to']}>
              {data.length === 0 ? 0 : 1}
              {' – '}
              {data.length}{' '}
              <span className={styles['pagination__from-text']}>
                {t('common::pagination.of')}
              </span>{' '}
              {pagination.total}
            </span>
          )}
          <Container
            display={'flex'}
            flexAlign={'center'}
            flexJustify={'center'}
            className={styles['show-more__container']}
          >
            {pagination.total > data.length && (
              <Button
                onClick={() => changePage(1)}
                className={styles['show-more__button']}
                variant={'link'}
                color={'Label-GU-Primary'}
                startIcon={
                  loading ? <Spinner width={16} color={'inherit'} /> : null
                }
                endIcon={
                  <Icon name={'arrow1-down'} color={'inherit'} size={'s'} />
                }
              >
                {t('common::pagination.show_more')}
              </Button>
            )}
          </Container>
        </div>
      )}
    </>
  )
}

TableComponent.propTypes = {
  data: PropTypes.array,
  pagination: PropTypes.object,
  loading: PropTypes.bool,
  filters: PropTypes.object,
  dropTableData: PropTypes.func,
  onTableParamsUpdate: PropTypes.func,
  groupBy: PropTypes.string,
  limit: PropTypes.number,
  tableSwitchers: PropTypes.array,
  exportButtons: PropTypes.array,
  columns: PropTypes.array,
  initialState: PropTypes.object,
  isDemoMode: PropTypes.bool,
  onSortingChange: PropTypes.func,
}

export default TableComponent
